import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import DataTable from '../components/ResponsiveDataTable';
import { getAllPayments, createManualPayment, getUnifiedTransactionHistory } from '../config/api';
import { toast } from 'react-toastify';
import { saveBlob, toCsvCell } from '../utils/nativeFiles';
import useIsMobile from '../hooks/useIsMobile';
import PromoWallet from './PromoWallet';

const Payments = () => {
  const isMobile = useIsMobile();
  // Which tab is active in the combined ledger view: 'ledger' | 'promo'.
  const [activeTab, setActiveTab] = useState('ledger');
  // Measured scroll height for the table so it fills exactly the space left
  // below the filter row — no hardcoded vh guess, no trailing gap.
  const tableRegionRef = useRef(null);
  const [tableScrollHeight, setTableScrollHeight] = useState('60vh');
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchPhone, setSearchPhone] = useState('');
  const [userBalance, setUserBalance] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showManualPaymentForm, setShowManualPaymentForm] = useState(false);
  const [manualPaymentData, setManualPaymentData] = useState({
    userPhone: '',
    orderId: '',
    paymentType: 'MANUAL_PAYMENT',
    paymentMode: '',
    referenceNumber: '',
    amount: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [canDownload, setCanDownload] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [paymentGateway, setPaymentGateway] = useState('');
  const [userId, setUserId] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [promoBalance, setPromoBalance] = useState(0);

  useEffect(() => {
    // Check if user has superAdmin role
    const userData = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
    if (!userData.access?.includes("finance")) {
      // Redirect to orders page if not superAdmin
      window.location.href = '/orders';
      return;
    }
    if (userData.role == 'superAdmin') {
      setIsSuperAdmin(true);
    }
    // Finance users (and superAdmin) can download the payments data.
    if (userData.role === 'superAdmin' || userData.access?.includes('finance')) {
      setCanDownload(true);
    }
    loadAllPayments();
  }, []);

  const loadAllPayments = async (filtersToUse = null) => {
    setLoading(true);
    try {
      const filters = {};
      const startDateVal = filtersToUse?.startDate !== undefined ? filtersToUse.startDate : startDate;
      const endDateVal = filtersToUse?.endDate !== undefined ? filtersToUse.endDate : endDate;
      const paymentGatewayVal = filtersToUse?.paymentGateway !== undefined ? filtersToUse.paymentGateway : paymentGateway;
      const userIdVal = filtersToUse?.userId !== undefined ? filtersToUse.userId : userId;
      
      if (startDateVal) filters.startDate = startDateVal;
      if (endDateVal) filters.endDate = endDateVal;
      if (paymentGatewayVal) filters.paymentGateway = paymentGatewayVal;
      if (userIdVal) filters.user_id = userIdVal;
      
      const response = await getAllPayments(1, 50, filters);
      if (response.success && response.data) {
        setPayments(response.data.entries || []);
      }
    } catch (error) {
      console.error('Error loading payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadData = async () => {
    setDownloading(true);
    try {
      const filters = {};
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;
      if (paymentGateway) filters.paymentGateway = paymentGateway;
      if (userId) filters.user_id = userId;
      
      const response = await getAllPayments(1, 10000, filters);
      if (response.success && response.data) {
        const dataToDownload = response.data.entries || [];
        
        // Prepare CSV data
        const headers = ['Date', 'User', 'Order ID', 'GST Number', 'Type', 'Amount', 'Payment gateway', 'Payment Mode', 'Reference', 'Description'];
        const rows = dataToDownload.map(entry => [
          formatDate(entry.created_at),
          entry.user?.name || 'N/A',
          entry.order_id || 'N/A',
          entry.gst_number || 'N/A',
          entry.entry_type,
          entry.amount,
          entry.payment_gateway || 'N/A',
          entry.payment_mode || 'N/A',
          entry.reference_number || 'N/A',
          entry.description || 'N/A'
        ]);
        
        // Create CSV content
        const csvContent = [
          headers.map(toCsvCell).join(','),
          ...rows.map(row => row.map(toCsvCell).join(','))
        ].join('\n');
        
        // Download CSV
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        await saveBlob(blob, `payments_${new Date().toISOString().split('T')[0]}.csv`);
        
        toast.success(`Downloaded ${dataToDownload.length} payment records`);
      }
    } catch (error) {
      console.error('Error downloading data:', error);
      toast.error('Failed to download payment data');
    } finally {
      setDownloading(false);
    }
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setPaymentGateway('');
    setUserId('');
    // Pass empty filters directly to avoid closure issue
    loadAllPayments({ startDate: '', endDate: '', paymentGateway: '', userId: '' });
  };

  const handleLedgerSearch = async () => {
    const phone = searchPhone.trim();
    if (!phone && !userId) {
      toast.error('Please enter a User ID or phone number');
      return;
    }

    if (phone && !/^[6-9]\d{9}$/.test(phone)) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    try {
      const response = await getUnifiedTransactionHistory(phone, 1, 50, userId);
      if (!response.success || !response.data?.user) {
        toast.error('User not found with the supplied details');
        setPayments([]);
        setUserBalance(0);
        setSelectedUser(null);
        setPromoBalance(0);
        return;
      }

      let entries = response.data.entries || [];
      if (startDate || endDate) {
        entries = entries.filter(entry => {
          const entryDate = new Date(entry.created_at);
          if (startDate && entryDate < new Date(startDate)) return false;
          if (endDate) {
            const endDateTime = new Date(endDate);
            endDateTime.setHours(23, 59, 59, 999);
            if (entryDate > endDateTime) return false;
          }
          return true;
        });
      }

      setPayments(entries);
      setUserBalance(response.data.balance || 0);
      setSelectedUser(response.data.user);
      setPromoBalance(response.data.promoBalance || 0);
    } catch (error) {
      console.error('Error searching user payments:', error);
      toast.error(error.message || 'Failed to search user payments');
      setPayments([]);
      setUserBalance(0);
      setSelectedUser(null);
      setPromoBalance(0);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchPhone('');
    setUserId('');
    setSelectedUser(null);
    setUserBalance(0);
    setPromoBalance(0);
    loadAllPayments({ userId: '' });
  };

  const handleManualPaymentSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!manualPaymentData.userPhone.trim()) {
      toast.error('User phone number is required');
      return;
    }
    if (!manualPaymentData.amount) {
      toast.error('Valid amount is required');
      return;
    }
    if (!manualPaymentData.paymentType) {
      toast.error('Payment type is required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await createManualPayment(manualPaymentData);
      if (response.success) {
        toast.success('Manual payment created successfully');
        setShowManualPaymentForm(false);
        setManualPaymentData({
          userPhone: '',
          orderId: '',
          paymentType: 'MANUAL_PAYMENT',
          paymentMode: '',
          referenceNumber: '',
          amount: '',
          description: ''
        });
        
        // Refresh the payments list
        if (selectedUser) {
          handleLedgerSearch();
        } else {
          loadAllPayments();
        }
      } else {
        toast.error(response.message || 'Failed to create manual payment');
      }
    } catch (error) {
      console.error('Error creating manual payment:', error);
      toast.error('Failed to create manual payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualPaymentInputChange = (e) => {
    const { name, value } = e.target;
    setManualPaymentData({
      ...manualPaymentData,
      [name]: value
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getEntryTypeColor = (entryType) => {
    const colors = {
      'ORDER': '#dc3545',
      'PAYMENT': '#28a745',
      'MANUAL_PAYMENT': '#007bff',
      'WALLET_TOPUP': '#17a2b8',
      'REFUND': '#ffc107',
      'ADJUSTMENT': '#6c757d',
      'PROMO_APPLIED': '#9b59b6'
    };
    return colors[entryType] || '#6c757d';
  };

  const columns = [
    {
      name: 'Date',
      selector: row => row.created_at,
      sortable: true,
      format: row => formatDate(row.created_at),
      width: '150px',
    },
    {
      name: 'User',
      selector: row => row.user?.name || 'N/A',
      sortable: true,
      format: row => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{row.user?.name || 'N/A'}</div>
          {/* <div style={{ fontSize: '12px', color: '#6c757d' }}>{row.user?.phone}</div> */}
        </div>
      ),
      width: '180px',
    },
    {
      name: 'Order ID',
      selector: row => row.order_id || 'N/A',
      sortable: true,
      format: row => row.order_id ? (
        <Link
          to={`/orders/${row.order_id}`}
          style={{ color: '#007bff', textDecoration: 'none' }}
          target={isMobile ? undefined : '_blank'}
        >
          {row.order_id}
        </Link>
      ) : 'N/A',
      width: '120px',
    },
    {
      name: 'GST Number',
      selector: row => row.gst_number || 'N/A',
      sortable: true,
      width: '160px',
    },
    {
      name: 'Type',
      selector: row => row.entry_type || row.type,
      sortable: true,
      format: row => {
        const entryType = row.entry_type || row.type || 'N/A';
        return (
          <span 
            style={{ 
              padding: '4px 8px', 
              borderRadius: '4px', 
              fontSize: '12px', 
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: getEntryTypeColor(entryType)
            }}
          >
            {entryType.replace('_', ' ')}
          </span>
        );
      },
      width: '150px',
    },
    {
      name: 'Amount',
      selector: row => row.amount,
      sortable: true,
      format: row => {
        const entryType = row.entry_type || row.type;
        return (
          <span style={{ 
            color: entryType === 'PROMO_APPLIED' ? '#9b59b6' : (parseFloat(row.amount) >= 0 ? '#28a745' : '#dc3545'),
            fontWeight: 'bold'
          }}>
            {formatCurrency(row.amount)}
          </span>
        );
      },
      width: '120px',
    },
    {
      name: 'Payment gateway',
      selector: row => row.payment_gateway || 'N/A',
      sortable: true,
      width: '120px',
    },
    {
      name: 'Payment Mode',
      selector: row => row.payment_mode || 'N/A',
      sortable: true,
      width: '120px',
    },
    {
      name: 'Reference',
      selector: row => row.reference_number || 'N/A',
      sortable: true,
      width: '120px',
    },
    {
      name: 'Description',
      selector: row => row.description || 'N/A',
      sortable: true,
      format: row => (
        <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {row.description || 'N/A'}
        </div>
      ),
      width: '200px',
    },
  ];

  // Measure the table region's top offset and size its scroll body to reach
  // the bottom of the viewport exactly. Recomputes on resize and tab change.
  useLayoutEffect(() => {
    const measure = () => {
      const el = tableRegionRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const PAGINATION = 56; // DataTable footer height
      const BOTTOM_PAD = 8;
      const h = Math.max(200, window.innerHeight - top - PAGINATION - BOTTOM_PAD);
      setTableScrollHeight(`${h}px`);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [activeTab, selectedUser]);

  // Shared filter-bar styling (kept in sync with PromoWallet's filter bar).
  // Flat & minimal: one light bar, each control a single subtle border, no
  // nested group boxes, no decorative icons.
  const filterBarStyle = {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px',
    padding: '8px 0', borderBottom: '1px solid #eef1f5',
  };
  const filterGroupStyle = { display: 'flex', alignItems: 'center', gap: '8px' };
  const filterInputStyle = { height: '32px', fontSize: '13px', padding: '0 10px', border: '1px solid #e2e8f0', borderRadius: '8px', outline: 'none', color: '#1f2937', background: '#fff' };
  const filterBtnStyle = { height: '32px', fontSize: '12px', fontWeight: 600, padding: '0 14px', borderRadius: '8px', flexShrink: 0 };
  const filterDividerStyle = { width: '1px', height: '20px', background: '#e2e8f0', flexShrink: 0, margin: '0 2px' };
  const filterIconStyle = { display: 'none' };
  const tabBtnStyle = (active) => ({
    padding: '6px 16px', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '13px',
    fontWeight: active ? 700 : 500,
    color: active ? '#0d6efd' : '#6c757d',
    background: active ? '#fff' : 'transparent',
    boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
    whiteSpace: 'nowrap',
  });

  // Segmented tab toggle — reused in the ledger top bar and passed into the
  // PromoWallet toolbar (leftSlot) so both tabs render a single filter row.
  const tabsToggle = (
    <div style={{ display: 'flex', background: '#eef1f5', borderRadius: '8px', padding: '2px', flexShrink: 0 }}>
      <button onClick={() => setActiveTab('ledger')} style={tabBtnStyle(activeTab === 'ledger')}>Ledger</button>
      <button onClick={() => setActiveTab('promo')} style={tabBtnStyle(activeTab === 'promo')}>Promo Wallet</button>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', padding: '10px 16px', boxSizing: 'border-box' }}>
      {/* TOP — one sleek row: tabs + filters + actions (ledger).
          On the promo tab, these same tabs are passed into PromoWallet's
          toolbar via leftSlot so promo is also a single row. */}
      {activeTab === 'ledger' && (
      <div style={{ ...filterBarStyle, flexWrap: 'nowrap', overflowX: 'auto', flexShrink: 0 }}>
        {tabsToggle}
        <span style={filterDividerStyle} />

        <input
          type="text"
          inputMode="numeric"
          value={userId}
          onChange={(e) => setUserId(e.target.value.replace(/\D/g, ''))}
          onKeyDown={(e) => { if (e.key === 'Enter') handleLedgerSearch(); }}
          placeholder="User ID"
          aria-label="User ID"
          style={{ ...filterInputStyle, width: '105px' }}
        />
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={searchPhone}
          onChange={(e) => setSearchPhone(e.target.value.replace(/\D/g, ''))}
          onKeyDown={(e) => { if (e.key === 'Enter') handleLedgerSearch(); }}
          placeholder="Phone"
          maxLength="10"
          style={{ ...filterInputStyle, width: '130px' }}
        />
        <button onClick={handleLedgerSearch} className="btn btn-primary" disabled={loading} style={filterBtnStyle}>Search</button>
        <button onClick={handleClearSearch} className="btn btn-secondary" disabled={loading} style={filterBtnStyle}>All</button>

        <span style={filterDividerStyle} />

        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} title="Start date" style={filterInputStyle} />
        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} title="End date" style={filterInputStyle} />
        <select value={paymentGateway} onChange={(e) => setPaymentGateway(e.target.value)} title="Payment gateway" style={{ ...filterInputStyle, cursor: 'pointer' }}>
          <option value="">All Gateways</option>
          <option value="cashfree">Cashfree</option>
          <option value="easebuzz">Easebuzz</option>
          <option value="promocash">Promocash</option>
        </select>
        <button onClick={() => loadAllPayments()} className="btn btn-primary" disabled={loading} style={filterBtnStyle}>Apply</button>
        <button onClick={handleClearFilters} className="btn btn-secondary" disabled={loading} style={filterBtnStyle}>Clear</button>

        {/* Actions + optional balance chip, pushed right */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto', flexShrink: 0 }}>
          {selectedUser && (
            <span style={{ fontSize: '12px', color: '#6c757d', whiteSpace: 'nowrap' }}>
              {selectedUser.name} · W <strong style={{ color: userBalance >= 0 ? '#16a34a' : '#dc2626' }}>{formatCurrency(userBalance)}</strong> · P <strong style={{ color: '#9b59b6' }}>{formatCurrency(promoBalance)}</strong>
            </span>
          )}
          {canDownload && (
            <button onClick={handleDownloadData} className="btn btn-info" disabled={downloading || loading} style={filterBtnStyle}>
              {downloading ? '…' : 'Download'}
            </button>
          )}
          <button onClick={() => setShowManualPaymentForm(true)} className="btn btn-success" style={filterBtnStyle}>+ Manual</button>
        </div>
      </div>
      )}

      {/* BOTTOM — table region. Height is measured so the table body reaches
          the bottom of the viewport exactly (no trailing gap). */}
      <div ref={tableRegionRef} style={{ marginTop: '8px' }}>
        {/* Promo Wallet tab: pass the tab toggle into its toolbar so promo is
            also a single row (tabs + filters + actions). */}
        {activeTab === 'promo' && <PromoWallet embedded leftSlot={tabsToggle} scrollHeight={tableScrollHeight} />}

        {activeTab === 'ledger' && (
          <DataTable
            columns={columns}
            data={payments}
            pagination
            paginationPerPage={20}
            paginationRowsPerPageOptions={[10, 20, 50, 100]}
            progressPending={loading}
            highlightOnHover
            striped
            responsive
            fixedHeader
            fixedHeaderScrollHeight={tableScrollHeight}
            noDataComponent="No payments found"
            customStyles={{
              headRow: {
                style: {
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #dee2e6',
                },
              },
              headCells: {
                style: {
                  fontWeight: 'bold',
                  fontSize: '14px',
                },
              },
              cells: {
                style: {
                  fontSize: '13px',
                },
              },
            }}
          />
        )}
      </div>
      {/* END bottom 85% */}

      {/* Manual Payment Modal */}
      {showManualPaymentForm && (
        <div className="modal-overlay" onClick={() => setShowManualPaymentForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Manual Payment</h3>
              <button 
                className="modal-close"
                onClick={() => setShowManualPaymentForm(false)}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleManualPaymentSubmit}>
              <div className="modal-body">
                <div className="order-info">
                  <div className="info-item">
                    <label className="info-label">User Phone *</label>
                    <input
                      type="tel"
                      inputMode="tel"
                      name="userPhone"
                      className="form-control"
                      value={manualPaymentData.userPhone}
                      onChange={handleManualPaymentInputChange}
                      placeholder="Enter 10-digit phone number"
                      maxLength="10"
                      required
                    />
                  </div>
                  
                  <div className="info-item">
                    <label className="info-label">Order ID</label>
                    <input
                      type="text"
                      name="orderId"
                      className="form-control"
                      value={manualPaymentData.orderId}
                      onChange={handleManualPaymentInputChange}
                      placeholder="Optional - Link to specific order"
                    />
                  </div>
                  
                  <div className="info-item">
                    <label className="info-label">Payment Type *</label>
                    <select
                      name="paymentType"
                      className="form-control"
                      value={manualPaymentData.paymentType}
                      onChange={handleManualPaymentInputChange}
                      required
                    >
                      <option value="MANUAL_PAYMENT">Manual Payment</option>
                      <option value="WALLET_TOPUP">Wallet Top-up</option>
                      <option value="REFUND">Refund</option>
                      <option value="ADJUSTMENT">Adjustment</option>
                    </select>
                  </div>
                  
                  <div className="info-item">
                    <label className="info-label">Payment Mode</label>
                    <select
                      name="paymentMode"
                      className="form-control"
                      value={manualPaymentData.paymentMode}
                      onChange={handleManualPaymentInputChange}
                    >
                      <option value="">Select Payment Mode</option>
                      <option value="CASH">Cash</option>
                      <option value="UPI">UPI</option>
                      <option value="CARD">Card</option>
                      <option value="NETBANKING">Net Banking</option>
                      <option value="CHEQUE">Cheque</option>
                      <option value="BANK_TRANSFER">Bank Transfer</option>
                    </select>
                  </div>
                  
                  <div className="info-item">
                    <label className="info-label">Reference Number</label>
                    <input
                      type="text"
                      name="referenceNumber"
                      autoCapitalize="characters"
                      autoCorrect="off"
                      spellCheck={false}
                      className="form-control"
                      value={manualPaymentData.referenceNumber}
                      onChange={handleManualPaymentInputChange}
                      placeholder="Transaction/Reference number"
                    />
                  </div>
                  
                  <div className="info-item">
                    <label className="info-label">Amount (₹) *</label>
                    <input
                      type="number"
                      name="amount"
                      inputMode="decimal"
                      className="form-control"
                      value={manualPaymentData.amount}
                      onChange={handleManualPaymentInputChange}
                      placeholder="Enter amount"
                      // min="0"
                      step="0.01"
                      required
                    />
                  </div>
                </div>
                
                <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                  <label className="info-label">Description</label>
                  <textarea
                    name="description"
                    className="form-control"
                    value={manualPaymentData.description}
                    onChange={handleManualPaymentInputChange}
                    rows="3"
                    placeholder="Optional description"
                    style={{ resize: 'vertical' }}
                  />
                </div>
              </div>
              
              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowManualPaymentForm(false)}
                  className="btn btn-secondary"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={submitting}
                >
                  {submitting ? 'Creating...' : 'Create Payment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;
